// Mirrors the backend's dot-notation permission values
// (api.tncid.app/src/webapp/common-components/models/user.ts + permissions.ts).
export enum UserPermissions {
  ALL = 'all',
  ANALYTICS = 'analytics.all',
  ANALYTICS_OVERVIEW = 'analytics.overview',
  ANALYTICS_REALTIME = 'analytics.realtime',
  // Sibling of ANALYTICS_REALTIME: reveals the masked identifiers of the realtime events.
  ANALYTICS_REALTIME_UNMASK = 'analytics.realtime_unmask',
  VISION = 'vision.all',
  REACH = 'reach.all',
  REACH_AUDIENCE = 'reach.audience',
  REACH_CUSTOM_IMPORT = 'reach.custom_import',
  REACH_GOAL = 'reach.goal',
  REACH_REPORTING = 'reach.reporting',
  SCRIPTS = 'scripts.all',
  CMP = 'cmp.all',
  ADMIN_DASHBOARD = 'admin_dashboard.all',
  SPECIAL_FEATURES = 'special_features.all',
  SPECIAL_FEATURES_CAN_IMPERSONATE = 'special_features.can_impersonate',
  SPECIAL_FEATURES_EXPERIMENTS = 'special_features.experiments',
  SPECIAL_FEATURES_CAN_BROADCAST = 'special_features.can_broadcast',
}

export type Permission = UserPermissions;

export const PERMISSIONS: readonly Permission[] = Object.values(UserPermissions);

export interface PermNode {
  all: boolean;
  children: Map<string, PermNode>;
}

export function newPermNode(): PermNode {
  return { all: false, children: new Map() };
}

// Single normalization point for every permission string, whether it comes from the
// JWT feed, the UserPermissions enum, or the user-editor. Hyphen/underscore and case
// differences between the backend and the UI can't produce divergent results because
// build and check both go through here.
export function splitPermission(perm: string): string[] {
  return perm.trim().toLowerCase().replace(/-/g, '_').split('.');
}

function insert(root: PermNode, path: string[]): void {
  let node = root;
  for (const part of path) {
    if (part === 'all') {
      node.all = true;
      return;
    }
    if (!node.children.has(part)) {
      node.children.set(part, newPermNode());
    }
    node = node.children.get(part)!;
  }
  node.all = true;
}

export function buildPermTree(raw: readonly string[]): PermNode {
  const root = newPermNode();
  for (const perm of raw) {
    insert(root, splitPermission(perm));
  }
  return root;
}

export function hasPermission(tree: PermNode, path: Permission | string): boolean {
  let node = tree;
  for (const part of splitPermission(path)) {
    if (node.all) return true;
    const next = node.children.get(part);
    if (!next) return false;
    node = next;
  }
  return node.all;
}

// Convenience wrapper for callers holding a raw permissions array (e.g. User.permissions)
// rather than a pre-built tree. A permission such as 'reach.all' implies every 'reach.*' leaf.
export function userHasPermission(userPermissions: readonly string[] | null | undefined, path: Permission | string): boolean {
  return hasPermission(buildPermTree(userPermissions ?? []), path);
}

// required.length === 0 means the caller didn't ask for any specific permission — allow.
export function userHasAnyPermission(userPermissions: readonly string[] | null | undefined, required: readonly (Permission | string)[]): boolean {
  if (!required || required.length === 0) return true;
  const tree = buildPermTree(userPermissions ?? []);
  return required.some(path => hasPermission(tree, path));
}
